import { BigNumber } from '@ethersproject/bignumber'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useChainId } from 'wagmi'
import { useActiveWeb3React } from '../../hooks'
import { classic } from '../../wagmi'
import { useBlockNumber } from '../application/hooks'
import { AppDispatch, AppState } from '../index'
import {
  addMulticallListeners,
  Call,
  removeMulticallListeners,
  parseCallKey,
  toCallKey,
  ListenerOptions
} from './actions'

// Types for ethers ABI interface (compatible with both ethers v5 and viem)
export interface EthersInterface {
  getFunction(nameOrSignatureOrSighash: string): FunctionFragment | null
  encodeFunctionData(functionFragment: FunctionFragment | string, values?: ReadonlyArray<unknown>): string
  decodeFunctionResult(functionFragment: FunctionFragment | string, data: string): Result
}

interface FunctionFragment {
  name: string
  type: string
  inputs: ReadonlyArray<unknown>
  outputs?: ReadonlyArray<unknown>
  stateMutability?: string
}

export interface EthersContract {
  address: string
  interface: EthersInterface
}

export interface Result extends ReadonlyArray<unknown> {
  readonly [key: string]: unknown
}

type MethodArg = string | number | bigint | BigNumber
type MethodArgs = Array<MethodArg | MethodArg[]>

type OptionalMethodInputs = Array<MethodArg | MethodArg[] | undefined> | undefined

function isMethodArg(x: unknown): x is MethodArg {
  if (['string', 'number', 'bigint'].indexOf(typeof x) !== -1) return true
  // Check for BigNumber-like object (has _isBigNumber property)
  if (x && typeof x === 'object' && '_isBigNumber' in x) return true
  return false
}

function isValidMethodArgs(x: unknown): x is MethodArgs | undefined {
  return (
    x === undefined ||
    (Array.isArray(x) && x.every(xi => isMethodArg(xi) || (Array.isArray(xi) && xi.every(isMethodArg))))
  )
}

interface CallResult {
  readonly valid: boolean
  readonly data: string | undefined
  readonly blockNumber: number | undefined
}

const INVALID_RESULT: CallResult = { valid: false, blockNumber: undefined, data: undefined }

// use this options object
export const NEVER_RELOAD: ListenerOptions = {
  blocksPerFetch: Infinity
}

// the lowest level call for subscribing to contract data
function useCallsData(calls: (Call | undefined)[], options?: ListenerOptions): CallResult[] {
  const { chainId } = useActiveWeb3React()
  const wagmiChainId = useChainId()
  // Use wagmi chainId as fallback, then default to ETC mainnet
  const effectiveChainId = chainId || wagmiChainId || classic.id
  const callResults = useSelector<AppState, AppState['multicall']['callResults']>(state => state.multicall.callResults)
  const dispatch = useDispatch<AppDispatch>()

  const serializedCallKeys: string = useMemo(
    () =>
      JSON.stringify(
        calls
          ?.filter((c): c is Call => Boolean(c))
          ?.map(toCallKey)
          ?.sort() ?? []
      ),
    [calls]
  )

  // update listeners when there is an actual change that persists for at least 100ms
  useEffect(() => {
    const callKeys: string[] = JSON.parse(serializedCallKeys)
    if (!effectiveChainId || callKeys.length === 0) return undefined
    const calls = callKeys.map(key => parseCallKey(key))
    dispatch(
      addMulticallListeners({
        chainId: effectiveChainId,
        calls,
        options
      })
    )

    return () => {
      dispatch(
        removeMulticallListeners({
          chainId: effectiveChainId,
          calls,
          options
        })
      )
    }
  }, [effectiveChainId, dispatch, options, serializedCallKeys])

  return useMemo(
    () =>
      calls.map<CallResult>(call => {
        if (!effectiveChainId || !call) return INVALID_RESULT

        const result = callResults[effectiveChainId]?.[toCallKey(call)]
        let data
        if (result?.data && result?.data !== '0x') {
          data = result.data
        }

        return { valid: true, data, blockNumber: result?.blockNumber }
      }),
    [callResults, calls, effectiveChainId]
  )
}

interface CallState {
  readonly valid: boolean
  // the result, or undefined if loading or errored/no data
  readonly result: Result | undefined
  // true if the result has never been fetched
  readonly loading: boolean
  // true if the result is not for the latest block
  readonly syncing: boolean
  // true if the call was made and is synced, but the return data is invalid
  readonly error: boolean
}

const INVALID_CALL_STATE: CallState = { valid: false, result: undefined, loading: false, syncing: false, error: false }
const LOADING_CALL_STATE: CallState = { valid: true, result: undefined, loading: true, syncing: true, error: false }

function toCallState(
  callResult: CallResult | undefined,
  contractInterface: EthersInterface | undefined,
  fragment: FunctionFragment | undefined,
  latestBlockNumber: number | undefined
): CallState {
  if (!callResult) return INVALID_CALL_STATE
  const { valid, data, blockNumber } = callResult
  if (!valid) return INVALID_CALL_STATE
  if (valid && !blockNumber) return LOADING_CALL_STATE
  if (!contractInterface || !fragment || !latestBlockNumber) {
    console.debug('[Multicall] Missing decode requirements', {
      hasInterface: !!contractInterface,
      hasFragment: !!fragment,
      latestBlock: latestBlockNumber
    })
    return LOADING_CALL_STATE
  }
  const success = data && data.length > 2
  const syncing = (blockNumber ?? 0) < latestBlockNumber
  let result: Result | undefined = undefined
  if (success && data) {
    try {
      result = contractInterface.decodeFunctionResult(fragment, data)
    } catch (err) {
      console.error('[Multicall] Decode failed', {
        fn: fragment.name,
        dataPreview: data.slice(0, 66),
        error: err
      })
      return {
        valid: true,
        loading: false,
        error: true,
        syncing,
        result
      }
    }
  }
  return {
    valid: true,
    loading: false,
    syncing,
    result: result,
    error: !success
  }
}

export function useSingleContractMultipleData(
  contract: EthersContract | null | undefined,
  methodName: string,
  callInputs: OptionalMethodInputs[],
  options?: ListenerOptions
): CallState[] {
  const fragment = useMemo(
    () => contract?.interface?.getFunction?.(methodName) ?? null,
    [contract, methodName]
  )

  // Debug call generation
  if (methodName === 'getEthBalance') {
    console.debug('[useSingleContractMultipleData]', methodName, {
      hasContract: !!contract,
      hasFragment: !!fragment,
      inputCount: callInputs?.length ?? 0
    })
  }

  const calls = useMemo(
    () =>
      contract && fragment && callInputs && callInputs.length > 0
        ? callInputs.map<Call>(inputs => {
            return {
              address: contract.address,
              callData: contract.interface.encodeFunctionData(fragment, inputs as ReadonlyArray<unknown>)
            }
          })
        : [],
    [callInputs, contract, fragment]
  )

  const results = useCallsData(calls, options)

  const latestBlockNumber = useBlockNumber()

  return useMemo(() => {
    const iface = contract ? contract.interface : undefined
    return results.map(result => toCallState(result, iface, fragment, latestBlockNumber))
  }, [fragment, contract, results, latestBlockNumber])
}

export function useMultipleContractSingleData(
  addresses: (string | undefined)[],
  contractInterface: EthersInterface,
  methodName: string,
  callInputs?: OptionalMethodInputs,
  options?: ListenerOptions
): CallState[] {
  const fragment = useMemo(
    () => contractInterface.getFunction?.(methodName) ?? null,
    [contractInterface, methodName]
  )
  const callData: string | undefined = useMemo(
    () =>
      fragment && isValidMethodArgs(callInputs)
        ? contractInterface.encodeFunctionData(fragment, callInputs as ReadonlyArray<unknown>)
        : undefined,
    [callInputs, contractInterface, fragment]
  )

  const calls = useMemo(
    () =>
      fragment && addresses && addresses.length > 0 && callData
        ? addresses.map<Call | undefined>(address => {
            return address && callData
              ? {
                  address,
                  callData
                }
              : undefined
          })
        : [],
    [addresses, callData, fragment]
  )

  const results = useCallsData(calls, options)

  const latestBlockNumber = useBlockNumber()

  return useMemo(() => {
    return results.map(result => toCallState(result, contractInterface, fragment, latestBlockNumber))
  }, [fragment, results, contractInterface, latestBlockNumber])
}

export function useSingleCallResult(
  contract: EthersContract | null | undefined,
  methodName: string,
  inputs?: OptionalMethodInputs,
  options?: ListenerOptions
): CallState {
  const fragment = useMemo(
    () => contract?.interface?.getFunction?.(methodName) ?? null,
    [contract, methodName]
  )

  const calls = useMemo<Call[]>(() => {
    return contract && fragment && isValidMethodArgs(inputs)
      ? [
          {
            address: contract.address,
            callData: contract.interface.encodeFunctionData(fragment, inputs as ReadonlyArray<unknown>)
          }
        ]
      : []
  }, [contract, fragment, inputs])

  const result = useCallsData(calls, options)[0]
  const latestBlockNumber = useBlockNumber()

  return useMemo(() => {
    const iface = contract ? contract.interface : undefined
    return toCallState(result, iface, fragment, latestBlockNumber)
  }, [result, contract, fragment, latestBlockNumber])
}
